import React, { useState } from 'react';
import { 
  Box, 
  Menu, 
  MenuItem, 
  IconButton, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { colors } from '../../styles/theme';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';

interface ConversationMenuProps {
  conversationId: string;
  thinkingType: 'reflection' | 'fraktal' | 'prompt-graph';
  onDeleteConversation: (conversationId: string, thinkingType: 'reflection' | 'fraktal' | 'prompt-graph') => void;
}

const ConversationMenu: React.FC<ConversationMenuProps> = ({
  conversationId,
  thinkingType,
  onDeleteConversation
}) => {
  const { t } = useUntypedTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const menuOpen = Boolean(menuAnchorEl);

  // Handle menu opening via three dots button
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent conversation selection when clicking menu
    setMenuAnchorEl(event.currentTarget);
  };

  // Close menu
  const handleMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent conversation selection when closing menu
    setMenuAnchorEl(null);
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent conversation selection when clicking delete
    setMenuAnchorEl(null);
    setConfirmDeleteOpen(true);
  };

  // Cancel delete
  const handleCancelDelete = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent conversation selection
    setConfirmDeleteOpen(false);
  };

  // Confirm delete
  const handleConfirmDelete = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent conversation selection
    setConfirmDeleteOpen(false);
    onDeleteConversation(conversationId, thinkingType);
  };

  return (
    <>
      {/* Three dots menu button */}
      <Tooltip title={t('history.conversationOptions', 'Gesprächsoptionen')}>
        <IconButton
          size="small"
          onClick={handleMenuClick}
          sx={{ 
            color: 'text.secondary',
            padding: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Menu from three dots button */}
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            mt: 1.5,
            width: 220,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
              borderRadius: '4px',
              mx: 0.5,
              '&:hover': {
                backgroundColor: `${colors.boneLight}`
              }
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('history.deleteConversation')}
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        onClick={(e) => e.stopPropagation()} // Prevent conversation selection
      >
        <DialogTitle id="alert-dialog-title">
          {t('history.deleteConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" component="div">
            {t('history.deleteConfirmMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('history.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t('history.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConversationMenu; 